import React from 'react';
import { connect } from 'react-redux';
import { Button, Stack, Typography } from '@mui/material';

import { getUserVisibleOrDefaultExceptionMessage, saveContent, saveTextContent } from '@paragon/client-tools';
import { sealingService } from '@paragon/data-connections';
import { hasSealsAndSignaturesPermission } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';

import { pFetch as pFetchJob } from '../../data/jobData';
import { RootState } from '../../store';
import { JobWithUsers } from '../../store/jobsSlice';

type StateProps = ReturnType<typeof mapStateToProps>;
interface OwnProps {
  jobSummary: JobWithUsers;
  jobId: number;
  miscFilesButtonVisible: boolean;
  originalZipUrl: string | null;
  separatedUrl: string | null;
  mergedUrl: string | null;
  onCloseClick: () => void;
}
type Props = StateProps & OwnProps;

interface State {
  preProcessedAfterJobsListLoaded: boolean;
}

class DownloadJob extends React.Component<Props, State> {
  state: State = {
    preProcessedAfterJobsListLoaded: false,
  };

  async componentDidMount() {
    if (this.getIsPreProcessed()) {
      return;
    }

    const jobFromServer = await pFetchJob(this.props.jobId);
    if (jobFromServer.hasBeenPreProcessed) {
      this.setState({ preProcessedAfterJobsListLoaded: true });
    }
  }

  getIsPreProcessed() {
    return this.props.jobSummary.hasBeenPreProcessed || this.state.preProcessedAfterJobsListLoaded;
  }

  getFilenameFromUrl(url: string | null) {
    return url?.split('/').at(-1) ?? null;
  }

  downloadMiscFiles = async () => {
    if (!this.props.miscFilesButtonVisible) {
      return;
    }

    try {
      const file = await sealingService.jobs.downloadMiscellaneous(this.props.jobId);
      file.download();
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading miscellaneous files'));
    }
  };

  downloadAllFiles = async () => {
    if (!this.getIsPreProcessed()) {
      return;
    }

    try {
      const file = await sealingService.jobs.downloadZip(this.props.jobId);
      file.download();
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading files'));
    }
  };

  generateFileContents() {
    return (
      'Job Summary: \r\n ' +
      '\r\nJob Id: ' +
      this.props.jobSummary.id +
      '\r\nFile Name: ' +
      this.props.jobSummary.name +
      '\r\nComponents: ' +
      this.props.jobSummary.numberOfDesigns +
      '\r\nOther Pdfs to seal: ' +
      this.props.jobSummary.numberOfMetaPdfs +
      '\r\nMiscellaneous files: ' +
      this.props.jobSummary.numberOfMiscFiles +
      '\r\nRegions: ' +
      this.props.jobSummary.regionsAsString +
      '\r\nJob Type: ' +
      this.props.jobSummary.jobType +
      '\r\nJob Priority: ' +
      this.props.jobSummary.jobPriority +
      '\r\nDesign Program: ' +
      this.props.jobSummary.designProgram +
      '\r\nDesign Code: ' +
      this.props.jobSummary.designCode +
      '\r\nOrganization Shared With: ' +
      this.props.jobSummary.sharedWithOrganizationsAsString +
      '\r\nComments: ' +
      this.props.jobSummary.comments +
      '\r\nCreator Phone: ' +
      this.props.jobSummary.creator!.phone +
      '\r\nCreator Email: ' +
      this.props.jobSummary.creator!.email +
      '\r\nContact Name: ' +
      this.props.jobSummary.contactName +
      '\r\nContact Phone: ' +
      this.props.jobSummary.contactPhone +
      '\r\nContact Email: ' +
      this.props.jobSummary.contactEmail +
      '\r\n\r\n///////////////////////////////////////////////////////\r\n' +
      '\r\nJob Status: ' +
      this.props.jobSummary.jobStatus +
      '\r\nLocation: ' +
      this.props.jobSummary.creator!.location +
      '\r\nCreated By ' +
      this.props.jobSummary.creator!.firstName +
      ' ' +
      this.props.jobSummary.creator!.lastName +
      '\r\nOn ' +
      new Date(this.props.jobSummary.createdTimestamp).toDateString() +
      '\r\n\r\n///////////////////////////////////////////////////////\r\n' +
      '\r\nLot Number: ' +
      this.props.jobSummary.lotNumber +
      '\r\nAddress: ' +
      this.props.jobSummary.address +
      '\r\nCity: ' +
      this.props.jobSummary.city +
      '\r\nSubdivision: ' +
      this.props.jobSummary.subdivision +
      '\r\nModel: ' +
      this.props.jobSummary.model +
      '\r\nBuilding Designer Name: ' +
      this.props.jobSummary.buildingDesignerName +
      '\r\nBuilding Designer Address: ' +
      this.props.jobSummary.buildingDesignerAddress +
      '\r\nBuilding Designer City: ' +
      this.props.jobSummary.buildingDesignerCity +
      '\r\nBuilding Designer State: ' +
      this.props.jobSummary.buildingDesignerState +
      '\r\nBuilding Designer License Number: ' +
      this.props.jobSummary.buildingDesignerLicenseNumber +
      '\r\nWind Code: ' +
      this.props.jobSummary.windCode +
      '\r\nWind Speed: ' +
      this.props.jobSummary.windSpeed +
      '\r\nWind Exposure: ' +
      this.props.jobSummary.windExposureCategory +
      '\r\nMean Roof Height: ' +
      this.props.jobSummary.meanRoofHeight +
      '\r\nRoof Load: ' +
      this.props.jobSummary.roofLoad +
      '\r\nFloor Load: ' +
      this.props.jobSummary.floorLoad +
      '\r\nCustomer Name: ' +
      this.props.jobSummary.customerName
    );
  }

  render() {
    const { mergedUrl, originalZipUrl, separatedUrl } = this.props;

    return (
      <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography sx={{ m: 2 }} variant="h5">
          Download Job
        </Typography>
        <Button
          sx={{ m: 1, width: 250 }}
          variant="outlined"
          disabled={mergedUrl == null}
          onClick={() => {
            if (mergedUrl != null) {
              saveContent(this.getFilenameFromUrl(mergedUrl) ?? 'merged.pdf', mergedUrl);
            }
          }}
        >
          Merged Sealed PDF
        </Button>
        <Button
          sx={{ m: 1, width: 250 }}
          variant="outlined"
          disabled={separatedUrl == null}
          onClick={() => {
            if (separatedUrl != null) {
              saveContent(this.getFilenameFromUrl(separatedUrl) ?? 'separated.zip', separatedUrl);
            }
          }}
        >
          Separated Sealed PDFs
        </Button>
        {(this.props.hasSealsAndSignaturesPermission || this.props.jobSummary.stampedByEmail) && (
          <Button
            sx={{ m: 1, width: 250 }}
            variant="outlined"
            disabled={!this.getIsPreProcessed()}
            onClick={this.downloadAllFiles}
          >
            All Files
          </Button>
        )}
        <Button
          sx={{ m: 1, width: 250 }}
          variant="outlined"
          disabled={!this.props.miscFilesButtonVisible}
          onClick={this.downloadMiscFiles}
        >
          Miscellaneous Files
        </Button>
        <Button
          sx={{ m: 1, width: 250 }}
          variant="outlined"
          disabled={originalZipUrl == null}
          onClick={() => {
            if (originalZipUrl != null) {
              saveContent(this.getFilenameFromUrl(originalZipUrl) ?? 'original.zip', originalZipUrl);
            }
          }}
        >
          Original Files
        </Button>
        <Button
          sx={{ m: 1, width: 250 }}
          variant="outlined"
          onClick={() => {
            saveTextContent(`Job_Summary${this.props.jobId.toString()}.txt`, this.generateFileContents());
          }}
        >
          Job Summary
        </Button>
        <Button sx={{ m: 2, width: 250 }} variant="contained" onClick={this.props.onCloseClick}>
          Close
        </Button>
      </Stack>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  hasSealsAndSignaturesPermission: hasSealsAndSignaturesPermission(state),
  token: state.logInData.loggedInUser?.token,
});

export default connect(mapStateToProps)(DownloadJob);
